/**
 * Document Lineage — "Version History". Port of
 * `omega-club/dashboard/src/components/DocumentLineage.jsx` (CHAP-491).
 *
 * One of the two board surfaces that had no ChapterBuild counterpart at all.
 *
 * Two deliberate differences from the omega-club original:
 *
 *   1. Opening a source document. omega-club asks the viewer for a data-room
 *      key and then fetches a raw R2 key through its Worker. ChapterBuild
 *      already authenticates the session, so the server resolves each version's
 *      R2 key to a document id and this page reuses `useDownloadDocument` —
 *      the same signed-URL flow the Data Room and Renderings pages use. There
 *      is no key prompt, and no R2 key reaches the client.
 *   2. Versions with no matching document render as plain text rather than the
 *      omega-club "key required" lock. They are not gated — the file has simply
 *      never been uploaded. 13 of 23 resolve today.
 *
 * Version order: the stored arrays are authored oldest-first, which reads as a
 * changelog but buries the version that is actually current. Rendered newest
 * first (Jake, 2026-07-31), reversed on a copy so the query cache is never
 * mutated in place.
 */
import { useParams } from 'react-router-dom';
import { useDocumentLineage, type LineageVersion } from '../hooks/useDocumentLineage.js';
import { useDownloadDocument } from '../hooks/useDocuments.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';

function ClockIcon() {
  return (
    <svg
      className="w-16 h-16 text-white/30"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={0.75}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function DocLinkIcon() {
  return (
    <svg
      className="w-3.5 h-3.5 inline-block"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

function StatusBadge({ status }: { status: LineageVersion['status'] }) {
  const current = status === 'current';
  return (
    <span
      className={
        'text-[0.625rem] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ' +
        (current ? 'bg-orange/15 text-orange' : 'bg-gray-200 text-gray-700')
      }
    >
      {current ? 'Current' : 'Superseded'}
    </span>
  );
}

export default function DocumentLineage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useDocumentLineage(slug);
  const download = useDownloadDocument(slug);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={error.message} onRetry={() => void refetch()} />;

  const families = data?.families ?? [];

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="rounded-xl bg-navy text-white p-4 sm:p-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Version History</h2>
          <p className="text-white/70 text-sm mt-1 max-w-2xl">
            How the plan evolved. Every source document that has multiple versions,{' '}
            <strong>newest first</strong>, with what changed and the decision that drove it. The
            decisions live <em>between</em> versions; a flat document list hides them.
          </p>
        </div>
        <div className="hidden sm:block flex-shrink-0">
          <ClockIcon />
        </div>
      </div>

      <div className="rounded-lg border border-orange/30 bg-orange/5 p-3 sm:p-4">
        <p className="text-navy text-sm">
          <span className="font-semibold text-orange">Read the deltas first.</span> The suite count
          is unresolved across families - <strong>8</strong> (feasibility, the only demand-tested
          number) / <strong>15</strong> (current drawings) / <strong>19</strong> (June-12 alternate)
          / <strong>20</strong> (newest proforma) / <strong>21</strong> (model and live site until 7
          Sep 2026) / <strong>22</strong> (since the modeler&rsquo;s 7 Sep revision, which adds seven
          courtyard suites that appear in no drawing). The &ldquo;FINAL&rdquo; feasibility never
          refreshed its financials, the two newest models disagree by $3.3M, and the counsel price
          changed $135K &rarr; $153K.{/* legacy-ok CHAP-423: the retired $135K IS the delta being reported; a lineage summary cannot say a price changed without naming what it changed from. */}
        </p>
      </div>

      {families.length === 0 && (
        <p className="text-neutral text-sm">No document lineage has been recorded yet.</p>
      )}

      {families.map((fam) => (
        <div key={fam.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-100">
            <h3 className="text-navy font-bold text-base sm:text-lg">{fam.family}</h3>
            {fam.blurb && <p className="text-neutral text-xs sm:text-sm mt-0.5">{fam.blurb}</p>}
          </div>

          {fam.reveals && (
            <div className="px-4 sm:px-5 py-3 bg-amber-50 border-b border-amber-100">
              <p className="text-[0.8125rem] text-navy leading-relaxed">
                <span className="font-semibold">What this reveals: </span>
                {fam.reveals}
              </p>
            </div>
          )}

          <ol className="relative px-4 sm:px-5 py-4 space-y-4">
            {[...fam.versions].reverse().map((v, i, ordered) => (
              <li key={`${fam.id}-${v.label}`} className="relative pl-6">
                <span
                  className={
                    'absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ' +
                    (v.status === 'current' ? 'bg-orange' : 'bg-gray-300')
                  }
                  aria-hidden="true"
                />
                {i < ordered.length - 1 && (
                  <span
                    className="absolute left-[4.5px] top-4 bottom-[-1rem] w-px bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-navy font-semibold text-sm">{v.label}</span>
                  <StatusBadge status={v.status} />
                  <span className="text-neutral text-xs">{v.date}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-neutral">
                  {v.suites && (
                    <span>
                      Suites: <strong className="text-navy">{v.suites}</strong>
                    </span>
                  )}
                  {v.cost && (
                    <span>
                      Cost: <strong className="text-navy">{v.cost}</strong>
                    </span>
                  )}
                  {v.documentId && (
                    <button
                      type="button"
                      disabled={download.isPending}
                      onClick={() => download.mutate(v.documentId as string)}
                      className="text-orange hover:text-orange-dark font-medium inline-flex items-center gap-1 cursor-pointer disabled:opacity-60 disabled:cursor-wait"
                    >
                      <DocLinkIcon /> {download.isPending ? 'opening…' : 'open document'}
                    </button>
                  )}
                </div>
                {v.delta && (
                  <p className="text-[0.8125rem] text-gray-600 mt-1 leading-relaxed">{v.delta}</p>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}
